import type { Store } from "vuex";
import { createStore } from "vuex";
import type { LoadListPayload, LoadPayload, ResourcesState } from "./resources";
import { Resources } from "./resources";
import { NotificationResources } from "./notifications";
import { config } from "@/utils/config";
import type {
  User,
  MemberUser,
  Group,
  Offer,
  Need,
  Category,
  Currency,
  Account,
  AccountSettings,
  Member,
  Transfer,
  GroupSettings,
  CurrencySettings,
  Trustline,
  Notification
} from "@/store/model";
// Import logged-in user module
import type { UserState } from "./me";
import me from "./me";
import type { UIState } from "./ui";
import ui from "./ui";
import createPersistPlugin from "./persist";
export { storeReady } from "./persist";
import KError, { KErrorCode } from "@/KError";
import type { Topup, AccountTopupSettings, TopupSettings } from "../features/topup/model";

// Build modules for Social API:
const socialUrl = config.SOCIAL_URL;
// `groups` resource does not follow the general pattern for endpoints.
const groups = new (class extends Resources<Group, unknown> {
  collectionEndpoint = () => "/groups";
  resourceEndpoint = (groupCode: string) => `/${groupCode}`;
})("groups", socialUrl);

const groupSettings = new (class extends Resources<GroupSettings, unknown> {
  collectionEndpoint = () => {throw new KError(KErrorCode.ScriptError, "Group settings cannot be listed");};
  resourceEndpoint = (groupCode: string) => `/${groupCode}/settings`;
})("group-settings", socialUrl)

const members = new Resources<Member, unknown>("members", socialUrl);

class PostResources<T extends Offer | Need> extends Resources<T, unknown> {
  collectionEndpoint = (groupCode: string) => `/${groupCode}/posts`;
  resourceEndpoint = (groupCode: string, id: string) => `/${groupCode}/posts/${id}`;

  protected buildQuery(payload: LoadListPayload) {
    return super.buildQuery({
      ...payload,
      filter: { ...payload.filter, type: this.type }
    })
  }

  protected buildQueryKey(payload: LoadListPayload) {
    return super.buildQueryKey({
      ...payload,
      filter: { ...payload.filter, type: this.type }
    })
  }

  protected resourceUrl(payload: LoadPayload) {
    const url = super.resourceUrl(payload)
    return `${url}${url.includes("?") ? "&" : "?"}filter[type]=${this.type}`
  }
}

const offers = new PostResources<Offer>("offers", socialUrl);
const needs = new PostResources<Need>("needs", socialUrl);
const categories = new Resources<Category, unknown>("categories", socialUrl);
const users = new (class extends Resources<User, unknown> {
  collectionEndpoint = () => "/users";
  resourceEndpoint = (groupCode: string, id?: string) => id ? `/users/${id}` : "/users/me";
})("users", socialUrl);

const memberUsers = new Resources<MemberUser, unknown>("member-users", socialUrl);

// Build modules for Accounting API:
const accountingUrl = config.ACCOUNTING_URL;
// Build modules for Accounting API:
// `currencies` resource does not follow the general pattern for endpoints.
const currencies = new (class extends Resources<Currency, unknown> {
  collectionEndpoint = () => "/currencies";
  resourceEndpoint = (groupCode: string) => `/${groupCode}/currency`;
  /**
   * Defines the inverse of the external relation group -> currency, so 
   * actions to currencies module can be called with include=group.
   */
  inverseRelationships = () => ({
    group: {
      module: "groups",
      field: "currency"
    }
  })
})("currencies", accountingUrl);

const currencySettings = new (class extends Resources<CurrencySettings, unknown> {
  collectionEndpoint = () => {throw new KError(KErrorCode.ScriptError, "Currency settings cannot be listed");};
  resourceEndpoint = (groupCode: string) => `/${groupCode}/currency/settings`;
})("currency-settings", accountingUrl)

const accounts = new (class extends Resources<Account, unknown> {
  /**
   * Defines the inverse of the external relation member -> account, so 
   * actions to accounts module can be called with include=member.
   */
  inverseRelationships = () => ({
    member: {
      module: "members",
      field: "account"
    }
  })
})("accounts", accountingUrl);

const accountSettings = new (class extends Resources<AccountSettings, unknown> {
  collectionEndpoint = () => {throw new KError(KErrorCode.ScriptError, "Account settings cannot be listed");};
  resourceEndpoint = (groupCode: string, id: string) => `/${groupCode}/accounts/${id}/settings`;
})("account-settings", accountingUrl)

const transfers = new Resources<Transfer, unknown>("transfers", accountingUrl);

const trustlines = new Resources<Trustline, unknown>("trustlines", accountingUrl);


const notificationsUrl = config.NOTIFICATIONS_URL;
const notifications = new NotificationResources("notifications", notificationsUrl);

const modules = {
    // Logged-in user module
    me,
    // User interface module.
    ui,

    // Resource modules:

    // Remark: The names of the resource modules must
    // be equal to the type property of the resources they
    // represent.

    // Social API resource modules.
    users,
    "member-users": memberUsers,
    groups,
    members,
    offers,
    needs,
    categories,
    "group-settings": groupSettings,

    // Accounting API resource modules.
    currencies,
    "currency-settings": currencySettings,
    accounts,
    "account-settings": accountSettings,
    transfers,
    trustlines,

    // Notifications API resource module.
    notifications,
  }

if (import.meta.env.FEAT_TOPUP === 'true') {
  modules["topup-settings"] = new (class extends Resources<TopupSettings, unknown> {
    collectionEndpoint = () => {throw new KError(KErrorCode.ScriptError, "Topup settings cannot be listed");}
    resourceEndpoint = (groupCode: string) => `/${groupCode}/currency/topup-settings`;
  })("topup-settings", accountingUrl);
  
  modules["account-topup-settings"] = new (class extends Resources<AccountTopupSettings, unknown> {
    collectionEndpoint = () => {throw new KError(KErrorCode.ScriptError, "Account topup settings cannot be listed");}
    resourceEndpoint = (groupCode: string, id: string) => `/${groupCode}/accounts/${id}/topup-settings`;
  })("account-topup-settings", accountingUrl);

  modules["topups"] = new Resources<Topup, unknown>("topups", accountingUrl);
}

/*
 * If not building with SSR mode, you can
 * directly export the Store instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Store instance.
 */

export default createStore({
  modules,
  // It is generally advisable to enable strict mode in development. That checks state is
  // not mutated outside commit actions. However we're mutating nested records within
  // resource modules (eg we directly change the state outside of a commit when we delete
  // a resource), so we should address that before enabling strict mode.
  strict: false,
  plugins: import.meta.env.QUASAR_SERVER ? [] : [createPersistPlugin()]
});

declare module 'vue' {
  interface State {
    me: UserState
    ui: UIState
    users: ResourcesState<User>
    memberUsers: ResourcesState<MemberUser>
    groups: ResourcesState<Group>
    groupSettings: ResourcesState<GroupSettings>
    members: ResourcesState<Member>
    offers: ResourcesState<Offer>
    needs: ResourcesState<Need>
    categories: ResourcesState<Category>
    currencies: ResourcesState<Currency>
    currencySettings: ResourcesState<CurrencySettings>
    accounts: ResourcesState<Account>
    accountSettings: ResourcesState<AccountSettings>
    transfers: ResourcesState<Transfer>
    trustlines: ResourcesState<Trustline>
    notifications: ResourcesState<Notification>
  }
  interface ComponentCustomProperties {
    $store: Store<State>
  }
}
/*
export const parseResourceUrl = (url: string, type: string) : {baseUrl: string, group?: string, id?: string}|undefined => {
  if (["contacts", "members", "offers", "needs", "categories", "accounts", "transfers"].includes(type)) {
    const match = url.match(new RegExp(`^([.]*)/([a-zA-Z0-9]*)/(${type})/([^/]*)$`))
    if (match) {
      return {
        baseUrl: match[1],
        group: match[2],
        id: match[4]
      }
    }
  } else if (type === "currencies") {
    const match = url.match(new RegExp(`^([.]*)/([a-zA-Z0-9]*)/currency$`))
    if (match) {
      return {
        baseUrl: match[1],
        group: match[2],
      }
    }
  } else if (type === "users") {
    const match = url.match(new RegExp(`^([.]*)/users/([^/]*)$`))
    if (match) {
      return {
        baseUrl: match[1],
        id: match[2]
      }
    }
  } else if (type === "account-settings") {
    const match = url.match(new RegExp(`^([.]*)/([a-zA-Z0-9]*)/accounts/([^/]*)/settings$`))
    if (match) {
      return {
        baseUrl: match[1],
        group: match[2],
        id: match[3]
      }
    }
  } else if (type === "group") {
    const match = url.match(new RegExp(`^([.]*)/([^/]*)$`))
    if (match) {
      return {
        baseUrl: match[1],
        id: match[2]
      }
    }
  }
  return undefined
}
*/
